import "./commodities-mobile.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	edit_05,
	flag_01,
	plus,
	refreshCcw_03,
	searchSm,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EditCommodityModal } from "~/shared/components/commodities/edit-commodity-modal.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";

import type { CommodityWithDefault } from "../../hooks/use-commodities-data.ts";
import { useCommoditiesData } from "../../hooks/use-commodities-data.ts";
import { useCommodityDeletion } from "../../hooks/use-commodity-deletion.ts";
import { useCommoditySelection } from "../../hooks/use-commodity-selection.ts";

const formatDimensions = (commodity: Commodity): string =>
	[
		commodity.dimensionLength,
		commodity.dimensionWidth,
		commodity.dimensionHeight,
	]
		.map((dimension) => dimension ?? 0)
		.join(" x ");

const formatValue = (value: number | string | null | undefined): string =>
	value === null || value === undefined || value === "" ? "-" : String(value);

const formatBoolean = (value: boolean | null): string => {
	if (value === null) return "-";
	return value ? "Yes" : "No";
};

type CommodityMetricProps = {
	field: string;
	label: string;
	value: string;
};

const CommodityMetric = ({ field, label, value }: CommodityMetricProps) => (
	<div className={"commodities-mobile-metric"} data-field={field}>
		<span className={"commodities-mobile-metric-label"}>{label}</span>
		<span className={"commodities-mobile-metric-value"}>{value}</span>
	</div>
);

const formatWeight = (weight: number | null | undefined): string =>
	weight === null || weight === undefined ? "-" : `${weight}lbs.`;

const COMMODITY_CARD_METRICS: readonly {
	field: string;
	label: string;
	getValue: (commodity: CommodityWithDefault) => string;
}[] = [
	{ field: "dimensions", label: "Dimensions", getValue: formatDimensions },
	{
		field: "nmfc",
		label: "NMFC Code",
		getValue: (commodity) => formatValue(commodity.nmfc),
	},
	{
		field: "quantity",
		label: "Qty",
		getValue: (commodity) => formatValue(commodity.quantity),
	},
	{
		field: "freightClass",
		label: "Class",
		getValue: (commodity) => formatValue(commodity.freightClass?.code),
	},
	{
		field: "unitType",
		label: "Units",
		getValue: (commodity) => formatValue(commodity.unitType?.name),
	},
	{
		field: "weight",
		label: "Weight (lbs)",
		getValue: (commodity) => formatWeight(commodity.weight),
	},
	{
		field: "isStackable",
		label: "Stackable",
		getValue: (commodity) => formatBoolean(commodity.isStackable),
	},
	{
		field: "isHazmat",
		label: "Hazmat",
		getValue: (commodity) => formatBoolean(commodity.isHazmat),
	},
];

const CommodityCardBody = ({
	commodity,
}: {
	commodity: CommodityWithDefault;
}) => (
	<div className={"commodities-mobile-body commodities-mobile-detail-grid"}>
		{COMMODITY_CARD_METRICS.map((metric) => (
			<CommodityMetric
				key={metric.field}
				field={metric.field}
				label={metric.label}
				value={metric.getValue(commodity)}
			/>
		))}
	</div>
);

type CommodityCardProps = {
	commodity: CommodityWithDefault;
	onDelete: (commodity: CommodityWithDefault) => void;
	onSetDefault: (commodity: CommodityWithDefault, isDefault: boolean) => void;
	isConfigLoading: boolean;
	/** Picker mode (Saved Commodities modal): checkbox, edit action, full labels. */
	isPicker?: boolean;
	isSelected?: boolean;
	onSelect?: (id: number, selected: boolean) => void;
	onEdit?: (commodity: Commodity) => void;
};

const CommodityCard = ({
	commodity,
	onDelete,
	onSetDefault,
	isConfigLoading,
	isPicker = false,
	isSelected = false,
	onSelect,
	onEdit,
}: CommodityCardProps) => {
	const description = commodity.description || "Unnamed commodity";
	const setDefaultIcon = plus;

	return (
		<article
			className={`commodities-mobile-card${isPicker ? " commodities-mobile-card-picker" : ""}`}
			data-commodity-id={commodity.id}
			data-testid={"commodity-card"}
		>
			<header className={"commodities-mobile-card-header"}>
				{isPicker ? (
					<Checkbox
						aria-label={`Select commodity ${description}`}
						checked={isSelected}
						data-testid={"commodity-select"}
						disabled={isConfigLoading}
						onChange={(event) => onSelect?.(commodity.id, !!event.value)}
					/>
				) : null}
				<h2 className={"commodities-mobile-description"} title={description}>
					{description}
				</h2>
				{commodity.isDefault ? (
					<span className={"commodities-mobile-default-badge"}>Default</span>
				) : null}
				<div className={"commodities-mobile-header-actions"}>
					{commodity.isDefault ? (
						<Button
							aria-label={"Remove default"}
							data-testid={"commodity-set-default"}
							disabled={isConfigLoading}
							onClick={() => onSetDefault(commodity, false)}
							svgIcon={flag_01}
							variant={"tertiary"}
						/>
					) : (
						<Button
							aria-label={"Set as default"}
							data-testid={"commodity-set-default"}
							disabled={isConfigLoading}
							onClick={() => onSetDefault(commodity, true)}
							svgIcon={setDefaultIcon}
							variant={"tertiary"}
						/>
					)}
					{onEdit ? (
						<Button
							aria-label={"Edit commodity"}
							data-testid={"commodity-edit"}
							disabled={isConfigLoading}
							onClick={() => onEdit(commodity)}
							svgIcon={edit_05}
							variant={"tertiary"}
						/>
					) : null}
					<Button
						aria-label={"Delete"}
						data-testid={"commodity-delete"}
						disabled={isConfigLoading}
						onClick={() => onDelete(commodity)}
						svgIcon={trash_01}
						variant={"dangerTertiary"}
					/>
				</div>
			</header>

			<CommodityCardBody commodity={commodity} />
		</article>
	);
};

type CommoditiesMobileProps = {
	customerId: string;
	resetOn?: boolean;
	isModal?: boolean;
	onSetSelectedItems?: (rows: Commodity[]) => void;
};

export const CommoditiesMobile = ({
	customerId,
	resetOn = false,
	isModal = false,
	onSetSelectedItems,
}: CommoditiesMobileProps) => {
	const [searchText, setSearchText] = useState("");
	const [editingCommodity, setEditingCommodity] = useState<Commodity>();

	const {
		data,
		setData,
		isLoading,
		setIsLoading,
		isError,
		refetch,
		createUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId,
		isConfigLoading,
	} = useCommoditiesData(customerId);

	const { select, selectedItems, handleCellSelect } = useCommoditySelection({
		data,
		resetOn,
	});

	useEffect(() => {
		onSetSelectedItems?.(selectedItems);
	}, [selectedItems, onSetSelectedItems]);

	const {
		showConfirmation,
		itemToDelete,
		handleRemoveFromList,
		handleConfirmDelete,
		handleCancelDelete,
	} = useCommodityDeletion({
		setData,
		setIsLoading,
		resetOn,
		totalItems: data.length,
	});

	const filteredData = useMemo(() => {
		const normalizedSearch = searchText.trim().toLowerCase();
		if (!normalizedSearch) return data;

		return data.filter((commodity) =>
			[
				commodity.description,
				commodity.nmfc,
				commodity.freightClass?.code,
				commodity.unitType?.name,
				formatDimensions(commodity),
				commodity.quantity,
				commodity.weight,
				formatBoolean(commodity.isStackable),
				formatBoolean(commodity.isHazmat),
			]
				.filter((value) => value !== null && value !== undefined)
				.some((value) =>
					String(value).toLowerCase().includes(normalizedSearch),
				),
		);
	}, [data, searchText]);

	const addDefault = useCallback(
		async (id: number) => {
			await createUserConfig({
				id: 0,
				customerId: Number(customerId),
				employeeId: Number(employeeId),
				configType: ConfigurationType.CommodityDefault,
				configValue: String(id),
			});
		},
		[customerId, employeeId, createUserConfig],
	);
	const removeDefault = useCallback(
		async (id: number) => {
			const config = userConfigs.find(
				(c) =>
					c.configType === ConfigurationType.CommodityDefault &&
					c.customerId === Number(customerId) &&
					c.configValue === String(id),
			);
			if (config) {
				await deleteUserConfig(config.id);
			}
		},
		[customerId, deleteUserConfig, userConfigs],
	);
	const handleSetDefault = useCallback(
		(commodity: CommodityWithDefault, shouldBeDefault: boolean): void => {
			(shouldBeDefault
				? addDefault(commodity.id)
				: removeDefault(commodity.id)
			).catch(() => undefined);
		},
		[addDefault, removeDefault],
	);
	let commodityContent: ReactNode;
	if (isLoading) {
		commodityContent = (
			<div className={"commodities-mobile-state"} role={"status"}>
				Loading commodities...
			</div>
		);
	} else if (isError) {
		commodityContent = (
			<div className={"commodities-mobile-state"} role={"alert"}>
				<p>Unable to load commodity templates.</p>
				<Button
					data-testid={"commodities-mobile-retry"}
					onClick={() => {
						void refetch();
					}}
					variant={"secondary"}
				>
					Retry
				</Button>
			</div>
		);
	} else if (filteredData.length > 0) {
		commodityContent = (
			<div
				aria-label={"Commodity templates"}
				className={"commodities-mobile-list"}
			>
				{filteredData.map((commodity) => (
					<CommodityCard
						key={commodity.id}
						commodity={commodity}
						isConfigLoading={isConfigLoading}
						isPicker={isModal}
						isSelected={!!select[commodity.id]}
						onDelete={handleRemoveFromList}
						onEdit={setEditingCommodity}
						onSelect={isModal ? handleCellSelect : undefined}
						onSetDefault={handleSetDefault}
					/>
				))}
			</div>
		);
	} else {
		commodityContent = (
			<div className={"commodities-mobile-state"} role={"status"}>
				No matching commodity templates found.
			</div>
		);
	}

	const searchInput = (
		<input
			aria-label={"Search commodities"}
			className={"commodities-mobile-search"}
			data-testid={"commodities-mobile-search"}
			id={"commodity-search"}
			onChange={(event) => setSearchText(event.target.value)}
			placeholder={isModal ? "Search..." : "Search"}
			value={searchText}
		/>
	);

	return (
		<div
			className={`commodities-mobile${isModal ? " commodities-mobile-picker" : ""}`}
			data-testid={"commodities-mobile"}
		>
			{isModal ? (
				<div
					className={
						"commodities-mobile-search-input-wrapper commodities-mobile-picker-search"
					}
				>
					{searchInput}
					<SvgIcon
						className={"commodities-mobile-search-icon"}
						icon={searchSm}
					/>
				</div>
			) : (
				<section
					aria-label={"Commodity search"}
					className={"commodities-mobile-search-panel"}
				>
					<div className={"commodities-mobile-search-row"}>
						<Button
							aria-label={"Refresh commodities"}
							data-testid={"commodities-mobile-refresh"}
							onClick={() => {
								void refetch();
							}}
							svgIcon={refreshCcw_03}
							variant={"tertiary"}
						/>
						<div
							className={
								"commodities-mobile-search-input-wrapper commodities-mobile-page-search"
							}
						>
							<SvgIcon
								className={"commodities-mobile-search-icon"}
								icon={searchSm}
							/>
							{searchInput}
						</div>
					</div>
				</section>
			)}

			{commodityContent}

			{editingCommodity ? (
				<EditCommodityModal
					commodity={editingCommodity}
					onClose={() => setEditingCommodity(undefined)}
				/>
			) : null}

			{showConfirmation ? (
				<TraxModal
					cancelText={"Cancel"}
					confirmText={"Yes, Delete"}
					confirmVariant={"primary"}
					onCancel={handleCancelDelete}
					onClose={handleCancelDelete}
					onConfirm={handleConfirmDelete}
					title={"Confirm Deletion"}
					type={"confirmation"}
				>
					Are you sure you want to remove &quot;{itemToDelete?.description}
					&quot; from your saved commodities?
				</TraxModal>
			) : null}
		</div>
	);
};
